import { memo, useState, useRef, useEffect } from "react";
import { Typography } from "@/core/components/ui/typography";
import { cn } from "@/core/utils/utils";

interface ExpandableTextProps {
  text: string;
  maxLines?: number;
  className?: string;
  variant?: "bodyW1" | "bodyW2" | "bodyW3";
  textColor?: "primary" | "secondary" | "tertiary";
  gradientColor?: string;
}

const LINE_CLAMP_CLASSES: Record<number, string> = {
  1: "line-clamp-1",
  2: "line-clamp-2",
  3: "line-clamp-3",
  4: "line-clamp-4",
  5: "line-clamp-5",
  6: "line-clamp-6",
} as const;

/**
 * A component that displays text with line-clamping and a "show more" link
 * if the text overflows. The link appears at the end of the visible text.
 */
export const ExpandableText = memo(
  ({
    text,
    maxLines = 3,
    className,
    variant = "bodyW2",
    textColor = "secondary",
    gradientColor = "#ffffff",
  }: ExpandableTextProps) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const checkOverflow = () => {
        const element = textRef.current;
        if (!element) return;

        // Check if the content is overflowing by comparing scroll height to client height
        const isOverflow = element.scrollHeight > element.clientHeight;
        setIsOverflowing(isOverflow);
      };

      checkOverflow();
      // Re-check on window resize
      window.addEventListener("resize", checkOverflow);
      return () => window.removeEventListener("resize", checkOverflow);
    }, [text]);

    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      setIsExpanded(!isExpanded);
    };

    const lineClampClass =
      !isExpanded && LINE_CLAMP_CLASSES[maxLines]
        ? LINE_CLAMP_CLASSES[maxLines]
        : undefined;

    return (
      <div className="relative">
        <div
          ref={textRef}
          className={cn("whitespace-pre-wrap", lineClampClass, className)}
        >
          <Typography variant={variant} textColor={textColor}>
            {text}
          </Typography>
        </div>
        {isOverflowing && !isExpanded && (
          <>
            {/* Gradient fade overlay */}
            <div
              className="pointer-events-none absolute bottom-0 right-0 w-24"
              style={{
                height: "1rem",
                background: `linear-gradient(to right, transparent, ${gradientColor} 40%, ${gradientColor})`,
              }}
            />
            {/* Show more button positioned at the end */}
            <button
              onClick={handleToggle}
              className="absolute bottom-0 right-0 text-md text-interactive-text underline"
            >
              see more
            </button>
          </>
        )}
        {isExpanded && (
          <button
            onClick={handleToggle}
            className="mt-1 text-md text-interactive-text underline"
          >
            see less
          </button>
        )}
      </div>
    );
  },
);

ExpandableText.displayName = "ExpandableText";
